import { useState } from "react"
import { useDispatch } from "react-redux"
import { createFee, fetchFees, clearMessage } from "../store/slices/feeSlice"
import { Calculator, X } from "lucide-react"

// Valid values for inputs
const VALID_QUOTAS = ["Govt", "Private"]
const VALID_CATEGORIES = ["SC_ST", "GEN_OBC"]
const VALID_TRADE_TYPES = ["ITI", "POLYTECHNIC"]
const VALID_DURATIONS = [0.5, 1, 2]

// ITI fee calculation function
function calculateITI(input) {
  const notes = []
  const { quota, category, isTechnical, durationYears } = input

  if (!VALID_QUOTAS.includes(quota)) {
    throw new Error("Invalid quota")
  }
  if (!VALID_CATEGORIES.includes(category)) {
    throw new Error("Invalid category")
  }
  if (typeof isTechnical !== "boolean") {
    throw new Error("isTechnical must be a boolean")
  }
  if (!VALID_DURATIONS.includes(durationYears)) {
    throw new Error("Invalid duration")
  }

  if (quota === "Govt") {
    const total = category === "SC_ST" ? 300 : 780
    notes.push("Govt ITI fee is fixed by category.")
    notes.push(category === "SC_ST" ? "SC/ST = ₹300" : "GEN/OBC/EWS = ₹780")
    return { total, notes }
  } else {
    const perYear = isTechnical ? 18000 : 15000
    const total = perYear * durationYears
    notes.push(`Private ITI ${isTechnical ? "Technical" : "Non-Technical"} at ₹${perYear.toLocaleString("en-IN")}/year`)
    if (durationYears !== 1) {
      notes.push(`Duration × years: ${durationYears} → total ₹${total.toLocaleString("en-IN")}`)
    }
    return { total, notes }
  }
}

// Polytechnic fee calculation function
function calculatePolytechnic(quota) {
  if (!VALID_QUOTAS.includes(quota)) {
    throw new Error("Invalid quota")
  }
  const total = quota === "Govt" ? 18306 : 38500
  const notes = ["Polytechnic fee is fixed by quota.", quota === "Govt" ? "Govt = ₹18,306" : "Private = ₹38,500"]
  return { total, notes }
}

const AddFee = ({ setShowAddModal }) => {
  const dispatch = useDispatch()
  const [formLoading, setFormLoading] = useState(false)
  const [formData, setFormData] = useState({
    student_id: "",
    amount: "",
    status: "Pending",
    transaction_id: "",
    meta: "",
    tradeType: "",
    quota: "",
    category: "",
    isTechnical: false,
    durationYears: "",
    useCalculatedAmount: true,
    utr_number: "",
  })

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    })
  }

  const handleCalculateFee = () => {
    try {
      if (!VALID_TRADE_TYPES.includes(formData.tradeType)) {
        dispatch(clearMessage())
        dispatch({
          type: "fees/setMessage",
          payload: { text: "Please select a valid trade type.", type: "error" },
        })
        return
      }
      if (formData.tradeType === "ITI") {
        if (!formData.quota || !formData.category || formData.durationYears === "") {
          dispatch(clearMessage())
          dispatch({
            type: "fees/setMessage",
            payload: { text: "Please fill all ITI fields to calculate the fee.", type: "error" },
          })
          return
        }
        const duration = Number.parseFloat(formData.durationYears)
        if (!VALID_DURATIONS.includes(duration)) {
          dispatch(clearMessage())
          dispatch({ type: "fees/setMessage", payload: { text: "Invalid duration selected.", type: "error" } })
          return
        }
        const result = calculateITI({
          quota: formData.quota,
          category: formData.category,
          isTechnical: formData.isTechnical,
          durationYears: duration,
        })
        setFormData({
          ...formData,
          amount: result.total.toString(),
          meta: JSON.stringify({ calculationNotes: result.notes }),
        })
        dispatch(clearMessage())
        dispatch({
          type: "fees/setMessage",
          payload: { text: `Calculated fee: ₹${result.total.toLocaleString("en-IN")}`, type: "success" },
        })
      } else if (formData.tradeType === "POLYTECHNIC") {
        if (!formData.quota) {
          dispatch(clearMessage())
          dispatch({
            type: "fees/setMessage",
            payload: { text: "Please select a quota to calculate the fee.", type: "error" },
          })
          return
        }
        const result = calculatePolytechnic(formData.quota)
        setFormData({
          ...formData,
          amount: result.total.toString(),
          meta: JSON.stringify({ calculationNotes: result.notes }),
        })
        dispatch(clearMessage())
        dispatch({
          type: "fees/setMessage",
          payload: { text: `Calculated fee: ₹${result.total.toLocaleString("en-IN")}`, type: "success" },
        })
      }
    } catch (err) {
      dispatch(clearMessage())
      dispatch({ type: "fees/setMessage", payload: { text: `Calculation error: ${err.message}`, type: "error" } })
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setFormLoading(true)
    const submitData = {
      student_id: formData.student_id,
      amount: formData.amount,
      status: formData.status,
      transaction_id: formData.transaction_id,
      meta: formData.meta,
      utr_number: formData.utr_number,
    }
    dispatch(createFee(submitData))
      .unwrap()
      .then(() => {
        dispatch(fetchFees())
        setFormData({
          student_id: "",
          amount: "",
          status: "Pending",
          transaction_id: "",
          meta: "",
          tradeType: "",
          quota: "",
          category: "",
          isTechnical: false,
          durationYears: "",
          useCalculatedAmount: true,
          utr_number: "",
        })
        setShowAddModal(false)
      })
      .catch((err) => {
        dispatch({
          type: "fees/setMessage",
          payload: { text: `Error creating fee: ${err.message || "Unknown error"}`, type: "error" },
        })
      })
      .finally(() => setFormLoading(false))
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Calculator className="w-6 h-6 text-blue-600" />
          <h3 className="text-xl font-semibold text-foreground">Add New Fee</h3>
        </div>
        <button
          onClick={() => setShowAddModal(false)}
          className="p-2 text-gray-600 hover:text-gray-800"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Student ID:</label>
            <input
              type="number"
              name="student_id"
              value={formData.student_id}
              onChange={handleInputChange}
              required
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Trade Type:</label>
            <select
              name="tradeType"
              value={formData.tradeType}
              onChange={handleInputChange}
              required
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Trade Type</option>
              <option value="ITI">ITI</option>
              <option value="POLYTECHNIC">Polytechnic</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Quota:</label>
            <select
              name="quota"
              value={formData.quota}
              onChange={handleInputChange}
              required
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Quota</option>
              <option value="Govt">Govt</option>
              <option value="Private">Private</option>
            </select>
          </div>
          {formData.tradeType === "ITI" && (
            <>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Category:</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  required
                  disabled={formLoading}
                  className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Category</option>
                  <option value="SC_ST">SC/ST</option>
                  <option value="GEN_OBC">GEN/OBC</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Technical Track:</label>
                <input
                  type="checkbox"
                  name="isTechnical"
                  checked={formData.isTechnical}
                  onChange={handleInputChange}
                  disabled={formLoading}
                  className="h-5 w-5 text-blue-500 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-1">Duration (Years):</label>
                <select
                  name="durationYears"
                  value={formData.durationYears}
                  onChange={handleInputChange}
                  required
                  disabled={formLoading}
                  className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Duration</option>
                  <option value="0.5">0.5</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                </select>
              </div>
            </>
          )}
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Use Calculated Amount:</label>
            <input
              type="checkbox"
              name="useCalculatedAmount"
              checked={formData.useCalculatedAmount}
              onChange={handleInputChange}
              disabled={formLoading}
              className="h-5 w-5 text-blue-500 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Amount:</label>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              required
              disabled={formData.useCalculatedAmount || formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Status:</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleInputChange}
              required
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Pending">Pending</option>
              <option value="Paid">Paid</option>
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Transaction ID:</label>
            <input
              type="text"
              name="transaction_id"
              value={formData.transaction_id}
              onChange={handleInputChange}
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">UTR Number:</label>
            <input
              type="text"
              name="utr_number"
              value={formData.utr_number}
              onChange={handleInputChange}
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4 col-span-2">
            <label className="block text-gray-700 font-medium mb-1">Meta (Additional Info):</label>
            <textarea
              name="meta"
              value={formData.meta}
              onChange={handleInputChange}
              rows="3"
              disabled={formLoading}
              className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="mt-6 flex space-x-4">
          {formData.useCalculatedAmount && (
            <button
              type="button"
              onClick={handleCalculateFee}
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
              disabled={formLoading}
            >
              Calculate Fee
            </button>
          )}
          <button
            type="submit"
            className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition duration-200"
            disabled={formLoading}
          >
            Create Fee
          </button>
          <button
            type="button"
            onClick={() => setShowAddModal(false)}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition duration-200"
            disabled={formLoading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}

export default AddFee